import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  planProjectRevision,
  applyProjectRevision,
  parseProject,
  uid,
  type Project,
  type RevisionMatches,
  type RevisionDecisions,
  type RevisionPlan,
  type RevisionChange,
} from "@proof/core";
import { ProofCanvas, type ProofCanvasProps } from "@proof/ui";
import type { ImportReport } from "@proof/core";
import { Select, Button, Control } from "./components";
import { snapshotImportReport } from "./DocumentImport";

export interface RevisionReviewServices extends Pick<
  ProofCanvasProps,
  "loadAsset" | "loadFont" | "transformImage" | "assetUrl"
> {
  validateResources(
    project: Project,
    signal: AbortSignal,
  ): void | Promise<void>;
}
export interface RevisionReviewReport {
  version: 1;
  currentHash: string;
  incomingHash: string;
  importReport?: ImportReport;
  matches: Omit<RevisionPlan["matches"][number], "candidates">[];
  changes: RevisionChange[];
  decisions: RevisionDecisions;
}
export interface ProjectRevisionReviewProps {
  project: Project;
  baseline?: Project;
  incoming: Project;
  importReport?: ImportReport;
  services: RevisionReviewServices;
  onApply(
    project: Project,
    report: RevisionReviewReport,
    signal: AbortSignal,
  ): void | Promise<void>;
  onCancel(): void;
  onChooseRevision?: () => void;
  onReportDownload?: (report: RevisionReviewReport) => void;
}
export function downloadRevisionReview(report: RevisionReviewReport) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "revision-review.json";
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function bindRevisionReviewServices(
  input: RevisionReviewServices,
): RevisionReviewServices {
  if (!input || typeof input.validateResources !== "function")
    throw new TypeError("Provide a validateResources callback");
  const result: RevisionReviewServices = {
    validateResources: (project, signal) =>
      input.validateResources.call(input, structuredClone(project), signal),
  };
  for (const key of [
    "loadAsset",
    "loadFont",
    "transformImage",
    "assetUrl",
  ] as const) {
    if (input[key] !== undefined && typeof input[key] !== "function")
      throw new TypeError(`Provide a ${key} callback`);
    if (input[key]) (result as any)[key] = input[key]!.bind(input);
  }
  const blob = (value: Blob) => {
    if (
      !value ||
      !["[object Blob]", "[object File]"].includes(
        Object.prototype.toString.call(value),
      ) ||
      value.size > 20000000
    )
      throw new Error("Invalid preview image bytes");
    return new Blob([value], { type: value.type });
  };
  if (input.loadAsset)
    result.loadAsset = async (...args) => {
      try {
        return blob(await input.loadAsset!.call(input, ...args));
      } catch {
        throw new Error("Preview image unavailable");
      }
    };
  if (input.transformImage)
    result.transformImage = async (...args) => {
      try {
        return blob(
          await input.transformImage!.call(
            input,
            args[0],
            structuredClone(args[1]),
            args[2],
          ),
        );
      } catch {
        throw new Error("Preview image transform unavailable");
      }
    };
  if (input.loadFont)
    result.loadFont = async (...args) => {
      try {
        const value = await input.loadFont!.call(
          input,
          structuredClone(args[0]),
          args[1],
        );
        if (
          !ArrayBuffer.isView(value) ||
          Object.prototype.toString.call(value) !== "[object Uint8Array]" ||
          value.byteLength > 8000000
        )
          throw new Error();
        return new Uint8Array(
          value.buffer,
          value.byteOffset,
          value.byteLength,
        ).slice();
      } catch {
        throw new Error("Preview font unavailable");
      }
    };
  return result;
}
const unavailableAsset: NonNullable<
  ProofCanvasProps["loadAsset"]
> = async () => {
  throw new Error("Preview image loader unavailable");
};
const unavailableFont: NonNullable<ProofCanvasProps["loadFont"]> = async () => {
  throw new Error("Preview font loader unavailable");
};
const unavailableTransform: NonNullable<
  ProofCanvasProps["transformImage"]
> = async () => {
  throw new Error("Preview image transform unavailable");
};

export function ProjectRevisionReview(props: ProjectRevisionReviewProps) {
  const state = useMemo(() => {
    try {
      const baseline =
        props.baseline && parseProject(structuredClone(props.baseline));
      if (
        baseline &&
        (baseline.id !== props.project.id ||
          baseline.tenantId !== props.project.tenantId)
      )
        throw new TypeError(
          "Revision baseline identity does not match the current project",
        );
      return {
        incoming: parseProject(structuredClone(props.incoming)),
        baseline,
        report: props.importReport && snapshotImportReport(props.importReport),
        services: bindRevisionReviewServices(props.services),
        key: uid("revision-review"),
      };
    } catch (error) {
      return {
        error:
          error instanceof Error ? error.message : "Invalid revision setup",
      };
    }
  }, [
    props.incoming,
    props.services,
    props.importReport,
    props.baseline,
    props.project.id,
    props.project.tenantId,
  ]);
  return state.incoming && state.services ? (
    <RevisionSession
      {...props}
      key={state.key}
      incoming={state.incoming}
      services={state.services}
      importReport={state.report}
      baseline={state.baseline}
    />
  ) : (
    <p role="alert">{state.error}</p>
  );
}
const titles: Record<string, string> = {
  fields: "Variables",
  pages: "Pages and layers",
  parts: "Part overrides",
  outputs: "Output profiles",
  datasets: "Datasets",
  forms: "Reusable forms",
  styles: "Text styles",
  groups: "Form groups",
  tabs: "Form tabs",
  triggers: "Rules",
  formInstances: "Form instances",
  dynamicSources: "Dynamic sources",
  fontManifest: "Fonts",
};
const describe = (value: unknown) =>
  value === undefined
    ? "Not configured"
    : (typeof value === "string"
        ? value
        : JSON.stringify(value, null, 2)
      ).slice(0, 1600);
function RevisionSession({
  project,
  baseline,
  incoming,
  importReport,
  services,
  onCancel,
  onChooseRevision,
  onReportDownload = downloadRevisionReview,
  onApply,
}: ProjectRevisionReviewProps) {
  const [base] = useState(() => {
    const base = parseProject(structuredClone(baseline || project));
    if (base.id !== project.id || base.tenantId !== project.tenantId)
      throw new Error(
        "Revision baseline identity does not match the current project",
      );
    return base;
  });
  const [step, setStep] = useState(1),
    [matches, setMatches] = useState<RevisionMatches>({}),
    [decisions, setDecisions] = useState<RevisionDecisions>({}),
    [search, setSearch] = useState(""),
    [category, setCategory] = useState(""),
    [limit, setLimit] = useState(25),
    [page, setPage] = useState(0),
    [zoom, setZoom] = useState(1),
    [error, setError] = useState("");
  const [cancelled, setCancelled] = useState(false);
  const pendingResources = useRef<AbortController | undefined>(undefined);
  const [applying, setApplying] = useState(false),
    [applied, setApplied] = useState(false),
    [resourceAttempt, setResourceAttempt] = useState(0);
  const pendingApply = useRef<AbortController | undefined>(undefined);
  const latest = useRef({ project, onApply });
  latest.current = { project, onApply };
  const cancelApply = () => {
    pendingApply.current?.abort();
    pendingApply.current = undefined;
    setApplying(false);
  };
  const cancelReview = () => {
    pendingResources.current?.abort();
    cancelApply();
    setCancelled(true);
  };
  useEffect(() => {
    cancelApply();
    return () => {
      pendingApply.current?.abort();
      pendingApply.current = undefined;
    };
  }, [project, onApply]);
  const proofServices = useMemo(
    () => ({
      loadAsset:
        services.loadAsset ||
        (services.assetUrl ? undefined : unavailableAsset),
      loadFont: services.loadFont || unavailableFont,
      transformImage: services.transformImage || unavailableTransform,
      assetUrl: services.assetUrl,
    }),
    [services],
  );
  const automatic = useMemo<{ plan?: RevisionPlan; error?: string }>(() => {
    if (!incoming) return {};
    try {
      return { plan: planProjectRevision(base, incoming) };
    } catch (error) {
      return { error: (error as Error).message };
    }
  }, [base, incoming]);
  const planned = useMemo<{ plan?: RevisionPlan; error?: string }>(() => {
    if (!incoming) return {};
    if (automatic.error) return automatic;
    try {
      return {
        plan: Object.keys(matches).length
          ? planProjectRevision(base, incoming, matches)
          : automatic.plan,
      };
    } catch (error) {
      return { error: (error as Error).message };
    }
  }, [base, incoming, matches, automatic]);
  const candidate = useMemo<{ project?: Project; error?: string }>(() => {
    if (!planned.plan) return {};
    try {
      return {
        project: applyProjectRevision(project, planned.plan, decisions),
      };
    } catch (error) {
      return { error: (error as Error).message };
    }
  }, [project, planned.plan, decisions]);
  const [preview, setPreview] = useState<{
    definition?: Project;
    page?: number;
    zoom?: number;
    ready: boolean;
    error?: string;
  }>({ ready: false });
  const reportPreview = useCallback(
    (status: { ready: boolean; error?: string }) =>
      setPreview({ ...status, definition: candidate.project, page, zoom }),
    [candidate.project, page, zoom],
  );
  const [resources, setResources] = useState<{
    definition?: Project;
    ready: boolean;
    error?: string;
  }>({ ready: false });
  useEffect(() => {
    if (step !== 3 || !candidate.project) return;
    const controller = new AbortController(),
      definition = candidate.project;
    pendingResources.current = controller;
    setResources({ definition, ready: false });
    void (async () => {
      try {
        await Promise.resolve();
        if (controller.signal.aborted) return;
        await services.validateResources(
          structuredClone(definition),
          controller.signal,
        );
        if (!controller.signal.aborted)
          setResources({ definition, ready: true });
      } catch (error) {
        if (!controller.signal.aborted)
          setResources({
            definition,
            ready: false,
            error:
              "Document images or fonts are missing or unavailable. Check the resources and retry.",
          });
      }
    })();
    return () => {
      controller.abort();
      if (pendingResources.current === controller)
        pendingResources.current = undefined;
    };
  }, [step, candidate.project, services, resourceAttempt]);
  const plan = planned.plan || automatic.plan;
  const previewPage = Math.min(
    page,
    (candidate.project?.pages.length || 1) - 1,
  );
  const currentPage = base.pages.findIndex(
    (item) => item.id === candidate.project?.pages[previewPage]?.id,
  );
  const changeTitle = (change: RevisionChange) => {
    if (change.kind === "order")
      return `${({ nodes: "Layer", layoutGroups: "Layout group", flowRegions: "Flow region", fields: "Variable", pages: "Page", fontManifest: "Font" } as Record<string, string>)[change.collection.at(-1)!] || titles[change.collection[0]] || "Item"} order`;
    if (
      change.collection[0] === "parts" &&
      ["overrides", "systemValues"].includes(change.path[0])
    ) {
      const field = [...base.fields, ...(plan?.incoming.fields || [])].find(
        (field) => field.id === change.path[1],
      );
      if (field)
        return `${change.label.split(" · ")[0]} · ${change.path.map((key, index) => (index === 1 ? field.label || field.key : key)).join(" / ")}`;
    }
    return change.label;
  };
  const filteredMatches =
    plan?.matches.filter(
      (match) =>
        (!category || match.collection[0] === category) &&
        `${match.incomingName} ${match.collection.join(" ")}`
          .toLowerCase()
          .includes(search.toLowerCase()),
    ) || [];
  const changes =
    plan?.changes.filter(
      (change) =>
        (!category ||
          change.collection[0] === category ||
          (!change.collection.length && category === "project")) &&
        `${changeTitle(change)} ${change.kind}`
          .toLowerCase()
          .includes(search.toLowerCase()),
    ) || [];
  const selected =
    plan?.changes.filter((change) => decisions[change.id] === "incoming")
      .length || 0;
  const unresolved =
    plan?.matches.filter(
      (match) =>
        match.reason === "ambiguous" && !Object.hasOwn(matches, match.key),
    ).length || 0;
  const filters = (
    <div className="revision-filters">
      <Control label="Search revision">
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setLimit(25);
          }}
        />
      </Control>
      <Control label="Revision category">
        <Select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
            setLimit(25);
          }}
        >
          <option value="">All changes</option>
          {Object.entries(titles).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
          <option value="project">Project settings</option>
        </Select>
      </Control>
    </div>
  );
  const next = () => {
    setStep(step + 1);
    setSearch("");
    setCategory("");
    setLimit(25);
    setError("");
  };
  return (
    <section
      className="project-revision-review"
      aria-label="Merge document revision"
    >
      <fieldset
        className="revision-controls"
        disabled={applying || applied || cancelled}
      >
        <ol className="revision-steps" aria-label="Revision steps">
          {["Import", "Match items", "Review changes", "Preview and apply"].map(
            (label, index) => (
              <li
                key={label}
                aria-current={step === index ? "step" : undefined}
              >
                {index + 1}. {label}
              </li>
            ),
          )}
        </ol>
        <p>
          Merge into <strong>{base.name}</strong>. Your current draft and
          published versions are kept until you apply and save. Review uses a
          snapshot captured when the revision workflow started.
        </p>
        {planned.error && (
          <p role="alert" className="error-text">
            {planned.error}
          </p>
        )}
        {step === 1 && (
          <>
            <h3>Match imported items to the current project</h3>
            {importReport?.reconciliation && (
              <p role="status">
                Reconciled {importReport.reconciliation.changes} target edits
                against the{" "}
                {importReport.reconciliation.baseline === "packaged"
                  ? "packaged"
                  : "regenerated"}{" "}
                baseline from source revision{" "}
                {importReport.reconciliation.sourceRevision}. The native
                snapshot retains its configuration. Review all differences
                against your current draft before saving.
              </p>
            )}
            {!!importReport?.diagnostics.length && (
              <details>
                <summary>
                  Review {importReport.diagnostics.length} import notes
                </summary>
                <ul>
                  {importReport.diagnostics.map((note, index) => (
                    <li key={index}>{note.message}</li>
                  ))}
                </ul>
              </details>
            )}
            {onChooseRevision && (
              <Button
                onClick={() => {
                  cancelReview();
                  onChooseRevision();
                }}
              >
                Choose a different revision
              </Button>
            )}
            <Button
              onClick={() => {
                setMatches({});
                setDecisions({});
              }}
            >
              Reset matches
            </Button>
            <p>
              Stable IDs, source IDs and unique names suggest matches. Confirm
              ambiguous items or choose New item. Changing a match resets the
              change selections.
            </p>
            {filters}
            <div className="revision-table">
              <table>
                <thead>
                  <tr>
                    <th>Imported item</th>
                    <th>Current item</th>
                    <th>Match basis</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMatches.slice(0, limit).map((match) => (
                    <tr key={match.key}>
                      <td>
                        {match.incomingName}
                        <small>{titles[match.collection[0]]}</small>
                      </td>
                      <td>
                        <Select
                          aria-label={`Match ${match.incomingName}`}
                          value={
                            Object.hasOwn(matches, match.key)
                              ? matches[match.key] || ""
                              : match.currentId || ""
                          }
                          onChange={(event) => {
                            setMatches((previous) => {
                              const updated = {
                                ...previous,
                                [match.key]: event.target.value || null,
                              };
                              if (
                                match.collection.length === 1 &&
                                match.collection[0] === "pages"
                              )
                                for (const key of Object.keys(updated)) {
                                  const path = JSON.parse(key);
                                  if (
                                    path[0] === "pages" &&
                                    path[1] === match.incomingId &&
                                    path.length > 2
                                  )
                                    delete updated[key];
                                }
                              return updated;
                            });
                            setDecisions({});
                          }}
                        >
                          <option value="">New item</option>
                          {match.candidates.map((value) => (
                            <option key={value.id} value={value.id}>
                              {value.name}
                            </option>
                          ))}
                        </Select>
                        {match.reason === "ambiguous" &&
                          !Object.hasOwn(matches, match.key) && (
                            <Button
                              onClick={() => {
                                setMatches((previous) => ({
                                  ...previous,
                                  [match.key]: null,
                                }));
                                setDecisions({});
                              }}
                            >
                              Confirm new item
                            </Button>
                          )}
                      </td>
                      <td>{match.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredMatches.length > limit && (
              <Button onClick={() => setLimit(limit + 25)}>
                Show more matches
              </Button>
            )}
            {unresolved > 0 && (
              <p role="status">
                Resolve {unresolved} ambiguous matches before continuing.
              </p>
            )}
          </>
        )}
        {step === 2 && (
          <>
            <h3>Choose the changes to accept</h3>
            <p>
              Every change starts with Keep current. Check Use incoming for
              edits, additions or deletions you want. Dependent references must
              remain valid together.
            </p>
            {filters}
            <p role="status">
              {selected} of {plan?.changes.length || 0} incoming changes
              selected
            </p>
            <div className="button-row">
              <Button
                onClick={() =>
                  setDecisions((previous) => ({
                    ...previous,
                    ...Object.fromEntries(
                      changes.map((change) => [change.id, "incoming" as const]),
                    ),
                  }))
                }
              >
                Use incoming for shown changes
              </Button>
              <Button
                onClick={() =>
                  setDecisions((previous) => ({
                    ...previous,
                    ...Object.fromEntries(
                      changes.map((change) => [change.id, "current" as const]),
                    ),
                  }))
                }
              >
                Keep current for shown changes
              </Button>
            </div>
            <div className="revision-changes">
              {changes.slice(0, limit).map((change) => (
                <article key={change.id} className="revision-change">
                  <label>
                    <input
                      type="checkbox"
                      checked={decisions[change.id] === "incoming"}
                      onChange={(event) =>
                        setDecisions((previous) => ({
                          ...previous,
                          [change.id]: event.target.checked
                            ? "incoming"
                            : "current",
                        }))
                      }
                    />
                    <strong>Use incoming: {changeTitle(change)}</strong>
                    <span className="badge soft">
                      {
                        {
                          set: "Edit",
                          add: "Add",
                          remove: "Delete",
                          order: "Reorder",
                        }[change.kind]
                      }
                    </span>
                  </label>
                  <details>
                    <summary>Compare current and incoming</summary>
                    <div className="revision-comparison">
                      <div>
                        <h4>Current</h4>
                        <pre>{describe(change.before)}</pre>
                      </div>
                      <div>
                        <h4>Incoming</h4>
                        <pre>{describe(change.after)}</pre>
                      </div>
                    </div>
                  </details>
                </article>
              ))}
            </div>
            {!changes.length && <p>No differences match this filter.</p>}
            {changes.length > limit && (
              <Button onClick={() => setLimit(limit + 25)}>
                Show more changes
              </Button>
            )}
            {candidate.error && (
              <p role="alert" className="error-text">
                {candidate.error}
              </p>
            )}
          </>
        )}
        {step === 3 && candidate.project && !cancelled && (
          <>
            <h3>Preview the merged document</h3>
            <p>
              {selected} accepted changes. This preview uses default variable
              values. Review every page before applying the selected changes.
              Your current document is kept until the revision is accepted.
            </p>
            <div className="revision-filters">
              <Control label="Revision preview page">
                <Select
                  value={previewPage}
                  onChange={(event) => setPage(Number(event.target.value))}
                >
                  {candidate.project.pages.map((item, index) => (
                    <option key={item.id} value={index}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Control>
              <Control label="Revision preview zoom">
                <Select
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                >
                  {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map((value) => (
                    <option key={value} value={value}>
                      {value * 100}%
                    </option>
                  ))}
                </Select>
              </Control>
            </div>
            <div className="revision-comparison revision-preview">
              <section>
                <h4>Current</h4>
                {currentPage >= 0 ? (
                  <ProofCanvas
                    project={base}
                    values={{}}
                    pageIndex={currentPage}
                    sourcePageId={base.pages[currentPage].id}
                    zoom={zoom}
                    {...proofServices}
                  />
                ) : (
                  <p>New page: no current counterpart.</p>
                )}
              </section>
              <section>
                <h4>Merged draft</h4>
                <ProofCanvas
                  project={candidate.project}
                  values={{}}
                  pageIndex={previewPage}
                  sourcePageId={candidate.project.pages[previewPage].id}
                  zoom={zoom}
                  onStatus={reportPreview}
                  {...proofServices}
                />
              </section>
            </div>
            <Button
              onClick={() => {
                try {
                  onReportDownload(
                    structuredClone({
                      version: 1,
                      currentHash: plan!.currentHash,
                      incomingHash: plan!.incomingHash,
                      importReport,
                      matches: plan!.matches.map(
                        ({ candidates, ...match }) => match,
                      ),
                      changes: plan!.changes,
                      decisions,
                    }),
                  );
                } catch {
                  setError(
                    "The revision report could not be delivered. Retry or cancel this review.",
                  );
                }
              }}
            >
              Download revision review
            </Button>
            {preview.error && (
              <p role="alert" className="error-text">
                The merged preview could not load all resources. Check the
                imported images and fonts before applying.
              </p>
            )}
            {resources.definition === candidate.project &&
              !resources.ready &&
              !resources.error && (
                <p role="status">
                  Checking images and fonts across the document…
                </p>
              )}
            {resources.error && (
              <>
                <p role="alert" className="error-text">
                  {resources.error}
                </p>
                <Button
                  onClick={() => setResourceAttempt((value) => value + 1)}
                >
                  Retry resource check
                </Button>
              </>
            )}
          </>
        )}
        {error && (
          <p role="alert" className="error-text">
            {error}
          </p>
        )}
      </fieldset>
      {step !== 2 && candidate.error && (
        <p role="alert" className="error-text">
          {candidate.error}
        </p>
      )}
      {applied && <p role="status">Revision applied.</p>}
      {cancelled && <p role="status">Revision cancelled.</p>}
      <div className="button-row revision-footer">
        <Button
          onClick={() => {
            cancelReview();
            onCancel();
          }}
        >
          {applied || cancelled ? "Close revision" : "Cancel revision"}
        </Button>
        {step > 1 && (
          <Button
            disabled={applying || applied || cancelled}
            onClick={() => {
              setStep(step - 1);
              setError("");
            }}
          >
            Back
          </Button>
        )}
        {step < 3 ? (
          <Button
            kind="primary"
            disabled={
              applying ||
              applied ||
              cancelled ||
              !planned.plan ||
              !!candidate.error ||
              (step === 1 && unresolved > 0) ||
              (step === 2 && (!selected || !!candidate.error))
            }
            onClick={next}
          >
            {step === 1 ? "Review changes" : "Preview merged draft"}
          </Button>
        ) : (
          <Button
            kind="primary"
            disabled={
              applying ||
              applied ||
              cancelled ||
              !candidate.project ||
              !selected ||
              preview.definition !== candidate.project ||
              preview.page !== page ||
              preview.zoom !== zoom ||
              !preview.ready ||
              !!preview.error ||
              resources.definition !== candidate.project ||
              !resources.ready
            }
            onClick={() => {
              if (!plan || pendingApply.current) return;
              const controller = new AbortController();
              pendingApply.current = controller;
              setApplying(true);
              setError("");
              const currentProject = latest.current.project,
                accept = latest.current.onApply;
              void (async () => {
                let accepting = false;
                try {
                  const merged = applyProjectRevision(
                    currentProject,
                    plan,
                    decisions,
                  );
                  const review: RevisionReviewReport = {
                    version: 1,
                    currentHash: plan.currentHash,
                    incomingHash: plan.incomingHash,
                    importReport,
                    matches: plan.matches.map(
                      ({ candidates, ...match }) => match,
                    ),
                    changes: plan.changes,
                    decisions,
                  };
                  accepting = true;
                  await accept(
                    structuredClone(merged),
                    structuredClone(review),
                    controller.signal,
                  );
                  if (
                    !controller.signal.aborted &&
                    pendingApply.current === controller &&
                    latest.current.project === currentProject &&
                    latest.current.onApply === accept
                  )
                    setApplied(true);
                } catch (error) {
                  if (
                    !controller.signal.aborted &&
                    pendingApply.current === controller
                  )
                    setError(
                      accepting
                        ? "The revision could not be applied. Retry or cancel this review."
                        : error instanceof Error
                          ? error.message
                          : "Revision could not be applied",
                    );
                } finally {
                  if (pendingApply.current === controller) {
                    pendingApply.current = undefined;
                    setApplying(false);
                  }
                }
              })();
            }}
          >
            {applying ? "Applying revision…" : "Apply to draft"}
          </Button>
        )}
      </div>
    </section>
  );
}
